import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Eye, Calendar as CalendarIcon } from 'lucide-react'
import { format, isAfter, isBefore, startOfToday, compareAsc } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import GerarBoleto from './gerar-boleto'
import { useCurrentUser } from '@/features/auth/state/auth'

type Boleto = {
  id: string
  cnpj: string
  nome: string
  valor: number
  vencimento: string
  status: string
}

const boletosMock: Boleto[] = [
  {
    id: '1',
    cnpj: '12.345.678/0001-00',
    nome: 'Empresa A',
    valor: 1000.0,
    vencimento: '2024-03-15',
    status: 'Pendente'
  },
  {
    id: '2',
    cnpj: '98.765.432/0001-01',
    nome: 'Empresa B',
    valor: 1500.5,
    vencimento: '2024-03-20',
    status: 'Pago'
  },
  {
    id: '3',
    cnpj: '11.223.344/0001-02',
    nome: 'Empresa C',
    valor: 2000.75,
    vencimento: '2024-03-25',
    status: 'Vencido'
  },
  {
    id: '4',
    cnpj: '42274696002723',
    nome: 'Empresa D',
    valor: 3000.0,
    vencimento: '2024-04-01',
    status: 'Pendente'
  },
  {
    id: '5',
    cnpj: '88.999.000/0001-04',
    nome: 'Empresa E',
    valor: 2500.25,
    vencimento: '2024-04-05',
    status: 'Pago'
  },
  {
    id: '6',
    cnpj: '12.345.678/0001-00',
    nome: 'Empresa A',
    valor: 1050.0,
    vencimento: '2024-03-15',
    status: 'Pago'
  },
  {
    id: '7',
    cnpj: '42274696002723',
    nome: 'Empresa D',
    valor: 3000.0,
    vencimento: '2024-04-05',
    status: 'PendPagoente'
  }
]

type ListaBoletosProps = {
  cnpj?: string // Prop opcional para receber o CNPJ
}

export default function ListaBoletos({ cnpj }: ListaBoletosProps) {
  const [searchTerm] = useState<string>(cnpj || '') // Inicializar com o CNPJ se fornecido
  const [statusFilter, setStatusFilter] = useState<string>('Todos')
  const [dateFilter, setDateFilter] = useState<Date | undefined>(undefined)
  const [currentPage] = useState(1)
  const [showGerarBoleto, setShowGerarBoleto] = useState(false)
  const user = useCurrentUser()
  // Ordena os boletos pela data mais próxima ao carregar a tela
  const sortedBoletos = [...boletosMock].sort((a, b) => {
    return compareAsc(new Date(a.vencimento), new Date(b.vencimento))
  })

  // Filtrar boletos pelo termo de pesquisa, status e data
  const filteredBoletos = sortedBoletos.filter(boleto => {
    const matchesSearch =
      boleto.cnpj.includes(searchTerm) || boleto.nome.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'Todos' || boleto.status === statusFilter

    const today = startOfToday()
    const boletoDate = new Date(boleto.vencimento)
    const matchesDate = !dateFilter || (isAfter(boletoDate, dateFilter) && isBefore(boletoDate, today))

    return matchesSearch && matchesStatus && matchesDate
  })

  const itemsPerPage = 50

  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentBoletos = filteredBoletos.slice(startIndex, endIndex)

  const toggleGerarBoleto = () => {
    setShowGerarBoleto(!showGerarBoleto)
  }

  return (
    <div className="container mx-auto p-0 mt-4 ">
      <Card className="bg-background">
        <CardHeader>
          <CardTitle className="text-2xl font-bold  text-foreground">Lista de Boletos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 mb-4 justify-end">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Filtrar por Status" />
              </SelectTrigger>
              <SelectContent className="bg-background">
                <SelectItem value="Todos">Todos</SelectItem>
                <SelectItem value="Pendente">Pendente</SelectItem>
                <SelectItem value="Pago">Pago</SelectItem>
                <SelectItem value="Vencido">Vencido</SelectItem>
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={'outline'}
                  className={`w-full justify-start text-left font-normal bg-background ${!dateFilter && 'text-muted-foreground'}`}
                >
                  {dateFilter ? (
                    format(dateFilter, 'PPP', { locale: ptBR })
                  ) : (
                    <span>Filtrar por Data de Vencimento</span>
                  )}
                  <CalendarIcon className="mr-2 h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-background">
                <Calendar mode="single" selected={dateFilter} onSelect={setDateFilter} />
              </PopoverContent>
            </Popover>
          </div>
          <div className="w-full overflow-hidden border border-gray-200 rounded-lg shadow-lg">
            {/* 🔹 Container para permitir rolagem horizontal */}
            <div className="w-full overflow-x-auto">
              <Table className="w-full min-w-[800px]">
                {/* 🔹 Cabeçalho fixo */}
                <TableHeader className="bg--secondary">
                  <TableRow>
                    <TableHead className="text-foreground w-40">CNPJ</TableHead>
                    <TableHead className="text-foreground w-40">Nome</TableHead>
                    <TableHead className="text-foreground w-32">Valor</TableHead>
                    <TableHead className="text-foreground w-40">Vencimento</TableHead>
                    <TableHead className="text-foreground w-32">Status</TableHead>
                    {user?.typeCargo === 'ADMIN' && <TableHead className="text-foreground w-40">Ações</TableHead>}
                  </TableRow>
                </TableHeader>
              </Table>
            </div>

            {/* 🔹 Corpo Rolável (Vertical e Horizontal) */}
            <div className="max-h-[400px] overflow-auto">
              <div className="w-full min-w-[800px]">
                <Table className="w-full">
                  <TableBody>
                    {currentBoletos.map(boleto => (
                      <TableRow key={boleto.id}>
                        <TableCell className="w-40">{boleto.cnpj}</TableCell>
                        <TableCell className="w-40">{boleto.nome}</TableCell>
                        <TableCell className="w-32">R$ {boleto.valor.toFixed(2)}</TableCell>
                        <TableCell className="w-40">
                          {new Date(boleto.vencimento).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="w-32">{boleto.status}</TableCell>
                        {user?.typeCargo === 'ADMIN' && (
                          <TableCell className="w-40">
                            <Button
                              className="bg-primary text-primary-foreground hover:bg-primary-hover"
                              variant="outline"
                              size="sm"
                            >
                              <Eye className="mr-2 h-4 w-4 " />
                              Ver detalhes
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {showGerarBoleto && <GerarBoleto onClose={toggleGerarBoleto} />}
    </div>
  )
}
